import React from 'react';
import { Box, GlobalStyles } from '@mui/material';
import type { Editor } from '@tiptap/react';

import DocumentToolbar from './DocumentToolbar';
import TiptapDocumentCanvas, { type ZoomSetting } from './TiptapDocumentCanvas';
import TableContextMenu from './TableContextMenu';

interface DocumentEditorShellProps {
  editor: Editor | null;
  saving: boolean;
  dirty: boolean;
  canEdit?: boolean;
  docxExporting?: boolean;
  googleExporting?: boolean;
  pendingImageCount?: number;
  pageSize?: 'letter' | 'a4';
  printPageMargin: string;
  useStandardTable?: boolean;
  zoomSetting: ZoomSetting;
  onZoomChange: (next: ZoomSetting) => void;
  onPrintNow?: () => void;
  onDocxExport?: () => Promise<void> | void;
  onGoogleExport?: () => Promise<void> | void;
  onPendingImagesRefresh?: () => void;
  onPendingImagesAction?: () => void;
  uploadContext?: { userId: string; resourceId: string };
}

const DocumentEditorShell: React.FC<DocumentEditorShellProps> = ({
  editor,
  saving,
  dirty,
  canEdit = true,
  docxExporting = false,
  googleExporting = false,
  pendingImageCount = 0,
  pageSize = 'letter',
  printPageMargin,
  useStandardTable = false,
  zoomSetting,
  onZoomChange,
  onPrintNow,
  onDocxExport,
  onGoogleExport,
  onPendingImagesRefresh,
  onPendingImagesAction,
  uploadContext,
}) => (
  <Box
    className="document-editor-shell document-print-root"
    sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 200px)', minHeight: 600 }}
  >
    <GlobalStyles
      styles={{
        '@page': {
          size: pageSize === 'a4' ? '210mm 297mm' : '8.5in 11in',
          margin: printPageMargin,
        },
        '@media print': {
          html: {
            backgroundColor: '#fff',
          },
          body: {
            margin: 0,
            backgroundColor: '#fff',
          },
          '#root': {
            backgroundColor: '#fff',
          },
          '.app-header': {
            display: 'none !important',
          },
          '.app-scoped-ai': {
            display: 'none !important',
          },
          '.app-layout, .app-layout-main, .app-layout-content, .app-layout-container': {
            minHeight: 'auto !important',
            height: 'auto !important',
            overflow: 'visible !important',
          },
          '.app-layout-main': {
            display: 'block !important',
          },
          '.app-layout-container': {
            maxWidth: 'none !important',
            width: '100% !important',
          },
          '.document-editor-topbar': {
            display: 'none !important',
          },
          '.document-editor-save-error': {
            display: 'none !important',
          },
          '.document-editor-docx-error': {
            display: 'none !important',
          },
          '.document-editor-google-export-error': {
            display: 'none !important',
          },
          '.document-editor-toolbar': {
            display: 'none !important',
          },
          '.document-editor-shell': {
            height: 'auto !important',
            minHeight: 'auto !important',
          },
          '.document-print-root': {
            width: '100% !important',
            backgroundColor: '#fff !important',
          },
          '.document-editor-shell > div:last-child': {
            overflow: 'visible !important',
            position: 'static !important',
          },
          '.document-editor-page': {
            padding: '0 !important',
          },
          '.MuiContainer-root': {
            padding: '0 !important',
          },
          '.document-editor-canvas': {
            minHeight: 'auto !important',
            height: 'auto !important',
            overflow: 'visible !important',
            padding: '0 !important',
            backgroundColor: '#fff !important',
            display: 'block !important',
          },
          '.document-editor-zoom-wrapper': {
            transform: 'none !important',
            zoom: '1 !important',
            width: '100% !important',
            height: 'auto !important',
            position: 'static !important',
            overflow: 'visible !important',
          },
          '.document-editor-zoom-content': {
            transform: 'none !important',
            position: 'static !important',
            width: '100% !important',
          },
        },
      }}
    />
    <DocumentToolbar
      editor={editor}
      saving={saving}
      dirty={dirty}
      canEdit={canEdit}
      docxExporting={docxExporting}
      googleExporting={googleExporting}
      pendingImageCount={pendingImageCount}
      zoomSetting={zoomSetting}
      onZoomChange={onZoomChange}
      onPrintNow={onPrintNow}
      onDocxExport={onDocxExport}
      onGoogleExport={onGoogleExport}
      onPendingImagesRefresh={onPendingImagesRefresh}
      onPendingImagesAction={onPendingImagesAction}
      uploadContext={uploadContext}
    />
    <Box
      sx={{ flex: 1, overflow: 'hidden', position: 'relative', bgcolor: '#f1f3f4', minHeight: 0 }}
    >
      <TiptapDocumentCanvas
        editor={editor}
        pageSize={pageSize}
        useStandardTable={useStandardTable}
        zoomSetting={zoomSetting}
      />
      <TableContextMenu editor={editor} />
    </Box>
  </Box>
);

export default DocumentEditorShell;
